/**
 * @file Reactive bus between `AIProvider` (writer) and the inline approval
 * control inside `MessagesList` (reader). Lives in Valtio so writers and
 * readers stay decoupled and granular subscribers re-render only on the
 * field they care about.
 *
 * - `pending` — written by `AIProvider` when an `approvalRequired` SSE
 *   event arrives, cleared on resolve / finish / error / abort. The
 *   `InlineApproval` control in `MessagesList` subscribes via `useSnapshot`
 *   and renders an inline approve/cancel block off it.
 * - `submitting` — raised by whichever surface starts resolving (the chat
 *   card's resolve, or the review panel's approve/cancel for the whole
 *   Storage-save + POST window). Buttons on BOTH surfaces disable on it
 *   (UI elements must disable, not hide), making resolution single-flight;
 *   a failed POST resets it so the prompt is restored instead of dropped.
 * - `resolved` — an in-memory record of the last resolution that survives
 *   only until the next loop start / `clearMessages` (and is gone on reload):
 *   written by both resolution paths before `pending` clears, rendered by
 *   `MessagesList` as the "Approved"/"Cancelled" session card, and cleared
 *   alongside `pending` on the next loop start / `clearMessages`.
 */
import { proxy } from 'valtio';
import type { AgenticApprovalRequiredEvent } from '#types';

export const agentApprovalState = proxy<{
    pending: AgenticApprovalRequiredEvent | null;
    submitting: boolean;
    resolved: {
        toolCallId: string;
        summary: string;
        approved: boolean;
        counts?: Record<string, number>;
    } | null;
}>({
    pending: null,
    submitting: false,
    resolved: null,
});
